import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { loungeCopy } from "@/lib/loungeCopy";
import { resolveLoungeUrl, LOUNGE_LANGS, LOUNGE_INTRO_FIELDS } from "@/lib/loungeUrl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Save, Upload, ImageIcon } from "lucide-react";

// Editor Contenuti Lounge: testi intro (5 lingue), lingue attive, 4 immagini + hero.
export function LoungeContentTab({ page }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [languages, setLanguages] = useState(["it", "en", "fr", "de", "es"]);
  const [intro, setIntro] = useState({});
  const [grid, setGrid] = useState(["", "", "", ""]);
  const [hero, setHero] = useState("");
  const [heroTitle, setHeroTitle] = useState({});
  const [heroSub, setHeroSub] = useState({});
  const fileRefs = useRef({});

  const load = () => {
    setLoading(true);
    api.get(`/admin/lounge/settings/${page}`).then(({ data }) => {
      setLanguages(data.languages || ["it", "en", "fr", "de", "es"]);
      setHero(data.hero || "");
      const g = [...(data.grid || [])];
      while (g.length < 4) g.push("");
      setGrid(g.slice(0, 4));
      // pre-riempi titoli hero con override o default loungeCopy (per lingua)
      const titleKey = page === "press" ? "press_title" : "amb_title";
      const subKey = page === "press" ? "press_sub" : "amb_sub";
      const ht = {}, hs = {};
      LOUNGE_LANGS.forEach(({ code }) => {
        const d = loungeCopy(code);
        const ovT = (data.hero_title || {})[code];
        const ovS = (data.hero_sub || {})[code];
        ht[code] = ovT !== undefined && ovT !== "" ? ovT : (d[titleKey] || "");
        hs[code] = ovS !== undefined && ovS !== "" ? ovS : (d[subKey] || "");
      });
      setHeroTitle(ht); setHeroSub(hs);
      // pre-riempi intro con override o default loungeCopy
      const merged = {};
      LOUNGE_LANGS.forEach(({ code }) => {
        const def = loungeCopy(code);
        const ov = (data.intro || {})[code] || {};
        merged[code] = {};
        LOUNGE_INTRO_FIELDS.forEach(({ key }) => {
          merged[code][key] = ov[key] !== undefined ? ov[key] : def[key] || "";
        });
      });
      setIntro(merged);
    }).catch((e) => toast.error(formatApiError(e?.response?.data?.detail))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [page]);

  const toggleLang = (code) => {
    setLanguages((ls) => (ls.includes(code) ? ls.filter((l) => l !== code) : [...ls, code]));
  };
  const setIntroField = (lang, key, val) => setIntro((p) => ({ ...p, [lang]: { ...p[lang], [key]: val } }));

  const save = async () => {
    if (languages.length === 0) return toast.error("Seleziona almeno una lingua");
    setSaving(true);
    try {
      await api.put(`/admin/lounge/settings/${page}`, { languages, intro, grid, hero, hero_title: heroTitle, hero_sub: heroSub });
      toast.success("Contenuti salvati");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setSaving(false); }
  };

  const uploadImage = async (kind, index, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("kind", kind); fd.append("index", String(index)); fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/lounge/settings/${page}/image`, fd);
      if (kind === "hero") setHero(data.url);
      else setGrid((g) => g.map((v, i) => (i === index ? data.url : v)));
      toast.success("Immagine caricata");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  if (loading) return <div className="flex items-center gap-2 py-10 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>;

  return (
    <div className="space-y-8" data-testid="lounge-content-tab">
      {/* Lingue attive */}
      <section className="rounded-xl border border-border bg-brema-card p-5">
        <h3 className="mb-1 text-lg font-semibold">Lingue disponibili</h3>
        <p className="mb-4 text-sm text-muted-foreground">Scegli quali lingue compaiono nel selettore della pagina pubblica.</p>
        <div className="flex flex-wrap gap-4">
          {LOUNGE_LANGS.map(({ code, label }) => (
            <label key={code} className="flex items-center gap-2 text-sm" data-testid={`lounge-lang-toggle-${code}`}>
              <Checkbox checked={languages.includes(code)} onCheckedChange={() => toggleLang(code)} />
              {label}
            </label>
          ))}
        </div>
      </section>

      {/* Testi copertina (Hero) */}
      <section className="rounded-xl border border-border bg-brema-card p-5" data-testid="lounge-hero-texts">
        <h3 className="mb-1 text-lg font-semibold">Testi copertina (Hero)</h3>
        <p className="mb-4 text-sm text-muted-foreground">Titolo e sottotitolo mostrati sull'immagine di copertina, per ciascuna lingua.</p>
        <Tabs defaultValue="it">
          <TabsList>
            {LOUNGE_LANGS.map(({ code, label }) => (
              <TabsTrigger key={code} value={code} data-testid={`lounge-hero-lang-${code}`}>{label}</TabsTrigger>
            ))}
          </TabsList>
          {LOUNGE_LANGS.map(({ code }) => (
            <TabsContent key={code} value={code} className="mt-4 space-y-4">
              <div>
                <Label className="mb-1.5 block text-xs">Titolo Hero</Label>
                <Input value={heroTitle[code] || ""} onChange={(e) => setHeroTitle((p) => ({ ...p, [code]: e.target.value }))} data-testid={`lounge-hero-title-${code}`} />
              </div>
              <div>
                <Label className="mb-1.5 block text-xs">Sottotitolo Hero</Label>
                <Textarea rows={2} value={heroSub[code] || ""} onChange={(e) => setHeroSub((p) => ({ ...p, [code]: e.target.value }))} data-testid={`lounge-hero-sub-${code}`} />
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </section>

      {/* Testi introduttivi */}
      <section className="rounded-xl border border-border bg-brema-card p-5">
        <h3 className="mb-1 text-lg font-semibold">Testi introduttivi del brand</h3>
        <p className="mb-4 text-sm text-muted-foreground">Storia · Performance · Lifestyle, per ciascuna lingua.</p>
        <Tabs defaultValue="it">
          <TabsList>
            {LOUNGE_LANGS.map(({ code, label }) => (
              <TabsTrigger key={code} value={code} data-testid={`lounge-intro-lang-${code}`}>{label}</TabsTrigger>
            ))}
          </TabsList>
          {LOUNGE_LANGS.map(({ code }) => (
            <TabsContent key={code} value={code} className="mt-4 space-y-4">
              {LOUNGE_INTRO_FIELDS.map(({ key, label, area }) => (
                <div key={key}>
                  <Label className="mb-1.5 block text-xs">{label}</Label>
                  {area ? (
                    <Textarea rows={3} value={(intro[code] || {})[key] || ""} onChange={(e) => setIntroField(code, key, e.target.value)} data-testid={`lounge-intro-${code}-${key}`} />
                  ) : (
                    <Input value={(intro[code] || {})[key] || ""} onChange={(e) => setIntroField(code, key, e.target.value)} data-testid={`lounge-intro-${code}-${key}`} />
                  )}
                </div>
              ))}
            </TabsContent>
          ))}
        </Tabs>
      </section>

      {/* Immagini */}
      <section className="rounded-xl border border-border bg-brema-card p-5">
        <h3 className="mb-1 text-lg font-semibold">Immagini di presentazione</h3>
        <p className="mb-4 text-sm text-muted-foreground">Immagine hero + le 4 immagini quadrate della griglia. Carica un file o incolla un URL.</p>

        <div className="mb-6">
          <Label className="mb-1.5 block text-xs">Hero</Label>
          <div className="flex flex-wrap items-center gap-3">
            <div className="h-20 w-32 overflow-hidden rounded-lg border border-border bg-muted">
              {hero ? <img src={resolveLoungeUrl(hero)} alt="hero" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-muted-foreground"><ImageIcon className="h-5 w-5" /></div>}
            </div>
            <Input value={hero} onChange={(e) => setHero(e.target.value)} placeholder="URL immagine hero" className="max-w-md" data-testid="lounge-hero-url" />
            <input type="file" accept="image/*" hidden ref={(el) => (fileRefs.current["hero"] = el)} onChange={(e) => uploadImage("hero", 0, e.target.files?.[0])} />
            <Button variant="outline" size="sm" onClick={() => fileRefs.current["hero"]?.click()} data-testid="lounge-hero-upload"><Upload className="mr-1.5 h-4 w-4" /> Carica</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {grid.map((g, i) => (
            <div key={i} className="rounded-lg border border-border p-3" data-testid={`lounge-grid-slot-${i}`}>
              <Label className="mb-1.5 block text-xs">Immagine {i + 1}</Label>
              <div className="flex items-center gap-3">
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md border border-border bg-muted">
                  {g ? <img src={resolveLoungeUrl(g)} alt={`grid ${i}`} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-muted-foreground"><ImageIcon className="h-4 w-4" /></div>}
                </div>
                <div className="flex-1 space-y-2">
                  <Input value={g} onChange={(e) => setGrid((arr) => arr.map((v, idx) => (idx === i ? e.target.value : v)))} placeholder="URL immagine" data-testid={`lounge-grid-url-${i}`} />
                  <input type="file" accept="image/*" hidden ref={(el) => (fileRefs.current[`grid${i}`] = el)} onChange={(e) => uploadImage("grid", i, e.target.files?.[0])} />
                  <Button variant="outline" size="sm" onClick={() => fileRefs.current[`grid${i}`]?.click()} data-testid={`lounge-grid-upload-${i}`}><Upload className="mr-1.5 h-4 w-4" /> Carica</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-brema-red text-white hover:bg-brema-red/90" data-testid="lounge-content-save">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Salva contenuti
        </Button>
      </div>
    </div>
  );
}
